import { toOriginal } from "../PlayerCore/timeline.js"
import { projectCtas } from "../PlayerCore/timelineView.js"

/**
 * The "v2" player's own track builder — sibling of VideoPlayer/podcastTracks.js's
 * buildPodcastTracks(), relaxed to always include an audio track. buildPodcastTracks() returns
 * no tracks at all when there's no video, since v1's audio-only and video experiences are two
 * separate pages (Pages/Player.vue vs Pages/VideoPlayer.vue); PlayerV2 is one unified page for
 * both, so an audio-only episode must still be fully playable here, with the video track simply
 * omitted (PlayerShell then shows no Watch/Listen toggle at all, same as it would for any
 * single-track podcast).
 */
export function buildPodcastTracksV2({ podcast, episode, video, masterUrl, transcriptUrl }) {
    const artworkUrl = episode.artwork || podcast.artwork || null
    const matches = episode.timeline?.video_matches === true
    const insertions = episode.timeline?.insertions ?? []
    // Sorted by published time (stable): the stable tie-break for equal starts after projection.
    const published = [...(episode.chapters ?? [])].sort((a, b) => (a.from_time ?? 0) - (b.from_time ?? 0))
    const chapter = (source, start) => ({ id: String(source.id), title: source.title ?? "", start, image: source.image ?? null })
    const transcripts = (episode.transcripts ?? []).map((transcript) => ({ lang: transcript.lang, label: transcript.label, url: transcriptUrl(transcript.lang) }))

    const audioTrack = {
        id: `audio:${episode.hash_id}`,
        itemId: episode.hash_id,
        kind: "audio",
        title: episode.title,
        artworkUrl,
        media: { audio: { url: episode.audio?.url ?? "", duration: episode.audio?.duration ?? 0 } },
        chapters: published.map((source) => chapter(source, source.from_time ?? 0)),
        ctas: projectCtas(episode.ctas, { mode: "audio", insertions }),
        transcripts,
        timeline: { insertions: insertions.map((insertion) => ({ atMs: insertion.at_ms, durationMs: insertion.duration_ms })) },
    }

    if (!video?.available || !masterUrl) {
        return { tracks: [audioTrack], sameTimeline: false }
    }

    const videoTrack = {
        id: `video:${episode.hash_id}`,
        itemId: episode.hash_id,
        kind: "video",
        title: episode.title,
        artworkUrl,
        media: { hls: { masterUrl, duration: video.duration ?? 0 } },
        // Pro publishes chapters/CTAs on the assembled-audio timeline; the video track gets
        // them projected onto the original timeline only when the audio was taken from this
        // video (timeline.video_matches) — same reasoning as buildPodcastTracks().
        chapters: matches ? published.map((source) => chapter(source, toOriginal(source.from_time ?? 0, insertions))) : [],
        ctas: matches ? projectCtas(episode.ctas, { mode: "video", insertions }) : [],
        transcripts,
        timeline: { insertions: [] },
    }

    return { tracks: [videoTrack, audioTrack], sameTimeline: matches }
}
